import { t } from '../../services/locale';
import type { ProfileBooking, ProfileBranch } from './profileBooking.types';

export type ProfilePenalty = { enabled?: boolean; fee?: number };

export type ProfilePenalties = {
  NO_SHOW?: ProfilePenalty;
  LATE_CANCEL?: ProfilePenalty;
};

export type CancelMessage = {
  message: string;
  confirmButtonLabel: string;
  /** False when the copy only informs the member that cancelling is not possible. */
  hasConfirmAction: boolean;
};

const SECONDS_PER_HOUR = 3600;
const STANDARD_MESSAGE_KEY = 'WEB_CONFIRM_CANCEL_BOOKING_MESSAGE';
const CANT_CANCEL_KEY = 'WEB_CONFIRM_CANT_CANCEL_BOOKING_MESSAGE_LATE_CANCEL';

/** Port of `BranchService.getCurrentBranchPenalties`: fee comes back in major units. */
export function getBranchPenalties(branch: ProfileBranch | null | undefined): ProfilePenalties {
  const penalties: ProfilePenalties = {};
  (branch?.features?.booking?.penalty_settings ?? []).forEach((setting) => {
    if (setting?.type === 'NO_SHOW' || setting?.type === 'LATE_CANCEL') {
      penalties[setting.type] = { fee: (setting.amount ?? 0) / 100, enabled: setting.enabled };
    }
  });
  return penalties;
}

export function studioHasPenalties(penalties: ProfilePenalties | null | undefined): boolean {
  return !!(penalties?.NO_SHOW?.enabled || penalties?.LATE_CANCEL?.enabled);
}

function feeLabel(penalty: ProfilePenalty | undefined, currencySymbol: string): string {
  return penalty?.fee ? `${currencySymbol}${penalty.fee}` : '';
}

function confirmable(message: string): CancelMessage {
  return { message, confirmButtonLabel: t('WEB_CONFIRM_CANCEL_BOOKING'), hasConfirmAction: true };
}

function informational(message: string): CancelMessage {
  return { message, confirmButtonLabel: t('WEB_OK'), hasConfirmAction: false };
}

export type ResolveCancelMessageInput = {
  booking: ProfileBooking;
  branch: ProfileBranch | null | undefined;
  penalties: ProfilePenalties | null | undefined;
  currencySymbol: string;
  /** Unix seconds. */
  nowUnix: number;
};

/** Everything the decision tree needs, computed once from the raw input. */
type CancelContext = {
  bookingIsClass: boolean;
  isWaitlist: boolean;
  cancelWindow: number;
  lateCancellationEnabled: boolean;
  cancellingOutside: boolean;
  /** Formatted fee, '' when the penalty is disabled or has no fee. */
  noShowFee: string;
  lateCancelFee: string;
};

function enabledFee(penalty: ProfilePenalty | undefined, currencySymbol: string): string {
  return penalty?.enabled ? feeLabel(penalty, currencySymbol) : '';
}

function buildCancelContext(input: ResolveCancelMessageInput): CancelContext {
  const { booking, branch, penalties, currencySymbol, nowUnix } = input;
  const bookingFeatures = branch?.features?.booking;
  const cancelWindow = bookingFeatures?.booking_cancel_window ?? 0;
  const diffHours = ((booking.time_start ?? 0) - nowUnix) / SECONDS_PER_HOUR;
  return {
    bookingIsClass: booking.model === 'events',
    isWaitlist: booking.status === 'WAITING',
    cancelWindow,
    lateCancellationEnabled: Boolean(bookingFeatures?.late_cancellation_enabled),
    cancellingOutside: !(diffHours < cancelWindow),
    noShowFee: enabledFee(penalties?.NO_SHOW, currencySymbol),
    lateCancelFee: enabledFee(penalties?.LATE_CANCEL, currencySymbol),
  };
}

function standardMessage(): CancelMessage {
  return confirmable(t(STANDARD_MESSAGE_KEY));
}

function cantCancelMessage(context: CancelContext): CancelMessage {
  return informational(t(CANT_CANCEL_KEY, { cancellation_window: context.cancelWindow }));
}

function noShowMessage(context: CancelContext): CancelMessage {
  return confirmable(
    t('WEB_BOOKING_CANCEL_TEXT_1', { cancellation_window: context.cancelWindow, no_show: context.noShowFee })
  );
}

/** No penalties configured, or not a class: only the late-cancel window can block. */
function resolveWithoutPenalties(context: CancelContext): CancelMessage {
  const allowed = context.cancellingOutside || !context.bookingIsClass || context.lateCancellationEnabled;
  return allowed ? standardMessage() : cantCancelMessage(context);
}

function resolveOutsideWindow(context: CancelContext): CancelMessage {
  if (context.noShowFee) {
    return noShowMessage(context);
  }
  return confirmable(t('WEB_BOOKING_CANCEL_TEXT_2', { cancellation_window: context.cancelWindow }));
}

/** Inside the window and late cancellation is off: the member cannot cancel. */
function resolveInsideWindowBlocked(context: CancelContext): CancelMessage {
  if (context.noShowFee) {
    return informational(
      t('WEB_BOOKING_CANCEL_TEXT_6', { cancellation_window: context.cancelWindow, no_show: context.noShowFee })
    );
  }
  return cantCancelMessage(context);
}

/** Inside the window with late cancellation on: warn about whichever fees apply. */
function resolveInsideWindowWithFees(context: CancelContext): CancelMessage {
  const { cancelWindow, lateCancelFee, noShowFee } = context;
  if (lateCancelFee && noShowFee) {
    return confirmable(
      t('WEB_BOOKING_CANCEL_TEXT_3', { cancellation_window: cancelWindow, late_cancel: lateCancelFee, no_show: noShowFee })
    );
  }
  if (lateCancelFee) {
    return confirmable(t('WEB_BOOKING_CANCEL_TEXT_4', { cancellation_window: cancelWindow, late_cancel: lateCancelFee }));
  }
  if (noShowFee) {
    return noShowMessage(context);
  }
  return standardMessage();
}

/**
 * Port of `the profile dialog cancelMessageResolver`. Same decision tree and
 * dictionary keys; instead of mutating `vm.cancelConfirmButtonLabel` /
 * `vm.hasCancelConfirmButtonAction` it returns them alongside the message.
 */
export function resolveCancelMessage(input: ResolveCancelMessageInput): CancelMessage {
  const context = buildCancelContext(input);

  if (!studioHasPenalties(input.penalties) || !context.bookingIsClass) {
    return resolveWithoutPenalties(context);
  }
  if (context.isWaitlist) {
    return standardMessage();
  }
  if (context.cancellingOutside) {
    return resolveOutsideWindow(context);
  }
  if (!context.lateCancellationEnabled) {
    return resolveInsideWindowBlocked(context);
  }
  return resolveInsideWindowWithFees(context);
}
